import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./styles.css";
import annotationPlugin from "chartjs-plugin-annotation";
import { Button, Grid, CircularProgress, Box, Paper, Checkbox, FormControlLabel, Typography } from "@mui/material";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { PRESENTATION_RESOURCES } from "./talkTreeShared/PresentationGeneration/PresentationResources";
import ExploreMyTalk from './talkTreeShared/ExploreMyTalk/ExploreMyTalk';
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import { useTreeToSvgConverter } from "./talkTreeShared/PresentationGeneration/TreeToSvgConverter";
import { ArcElement } from 'chart.js';
import AccordionComponent from "./components/AccordionComponent";
import SessionTrees from "./talkTreeShared/Tree/SessionTrees";
import FlowerTable from "./talkTreeShared/Tree/components/ThreeFlowers";
import { formatTimestampInRegionTimezone, getTimeZoneForRegion } from "./talkTreeShared/Tree/TimezoneUtility";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useMediaQuery } from "@mui/material";
import { generateFlowerKeyAltText } from "./talkTreeShared/Tree/components/treeAltText";
import { API_BASE_URL } from "./api";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  ChartTooltip,
  Legend,
  Filler,
  annotationPlugin,
  ArcElement,
  ChartTooltip,
  Legend,
  ChartDataLabels,
);

// ── Layout constants ──────────────────────────────────
const TAB_CONTENT_PADDING = '24px';
const LEFT_PANEL_BG = '#FDFBFA';
const LEFT_PANEL_BORDER = '1px solid #e5e7eb';
const LEFT_PANEL_RADIUS = 2;

function getCompareImageStyle(screenWidth) {
  if (screenWidth > 1440) return { transform: 'scale(0.8)',  transformOrigin: 'center' };
  if (screenWidth > 1024) return { transform: 'scale(0.65)', transformOrigin: 'center' };
  if (screenWidth > 768)  return { transform: 'scale(0.5)',  transformOrigin: 'center' };
  return                         { transform: 'scale(0.6)',  transformOrigin: 'center' };
}

function useWindowSize() {
  const [size, setSize] = useState({ width: window.innerWidth });
  useEffect(() => {
    const handler = () => setSize({ width: window.innerWidth });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return size;
}

// ── Tab icons  ──────────────────────────────────────────────────
const IntroIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path fillRule="evenodd" clipRule="evenodd" d="M9.88765 23.2351C12.7329 17.8107 10.1066 14.8785 4.96143 13.0935C5.05974 12.7607 5.15835 12.4278 5.25667 12.0949C7.4937 12.1159 9.64471 12.5318 11.4873 14.3565C11.3321 11.3754 12.0655 9.23147 13.4068 7.66003C13.6726 7.78739 13.9381 7.91474 14.204 8.04181C13.4706 9.43927 13.1656 11.0499 13.3771 12.9173C14.0662 11.8396 14.755 11.3112 15.4441 10.8612C15.6704 11.0767 15.8968 11.2918 16.1235 11.5073C12.9535 14.4401 12.7572 18.4597 14.6261 23.2348H9.88736L9.88765 23.2351Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M12.4297 8.25097C10.508 9.90939 8.24434 9.07705 7.29688 6.14767C9.0446 4.9418 12.019 5.17575 12.4297 8.25097Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M14.873 8.8062C16.7951 10.4646 19.0587 9.63256 20.0062 6.70289C18.2585 5.49703 15.284 5.73127 14.873 8.8062Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M16.4863 10.9872C17.0291 12.0598 18.1732 12.1007 19.1204 10.9855C18.5722 10.1429 17.2246 9.71171 16.4863 10.9872Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M16.0881 12.8281C15.9798 14.6477 17.4512 15.5682 19.5493 14.8206C19.4744 13.2983 18.0342 11.7115 16.0881 12.8281Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M19.21 12.3342C20.154 13.2859 21.3801 12.94 22 11.4359C21.1292 10.7295 19.5532 10.7315 19.21 12.3342Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M5.55644 13.9953C5.89427 15.0874 5.13573 15.8652 3.74669 15.7199C3.56463 14.7819 4.20485 13.603 5.55644 13.9953Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M4.61664 12.5131C4.31368 14.2068 2.76002 14.7293 1 13.5842C1.40528 12.2038 3.07755 11.0628 4.61664 12.5131Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M4.92742 10.6515C4.34065 11.5899 3.2883 11.5333 2.51318 10.4327C3.08566 9.70521 4.35694 9.42123 4.92742 10.6515Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M6.41283 8.992C5.13097 10.0455 3.66533 9.46673 3.09229 7.53825C4.25296 6.77755 6.19247 6.97654 6.41283 8.992Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M6.76644 6.51367C4.52026 7.2232 2.90172 5.68957 3.17638 2.85201C5.10732 2.46398 7.55099 3.7483 6.76644 6.51367Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M10.4856 12.4108C9.08912 12.487 8.38802 11.367 8.96907 9.7771C10.1366 9.83907 11.349 10.9375 10.4856 12.4108Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M8.35006 4.93766C7.24626 4.6858 6.95302 3.65817 7.76272 2.55066C8.6573 2.85909 9.35354 3.98366 8.35006 4.93766Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M7.13376 11.6567C5.99966 11.0589 5.98194 9.84874 7.19549 8.87256C8.0835 9.47123 8.51336 10.9056 7.13376 11.6567Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M13.8323 7.24119C12.3635 6.46571 12.3412 4.8974 13.914 3.63354C15.0644 4.40988 15.6206 6.26843 13.8323 7.24119Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M15.4692 5.41166C15.1156 4.34167 15.8487 3.56079 17.2206 3.67819C17.4172 4.59893 16.8088 5.77325 15.4692 5.41166Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path fillRule="evenodd" clipRule="evenodd" d="M10.7503 4.75291C9.20524 3.98027 9.14579 2.34999 10.752 1C11.9659 1.78088 12.5872 3.70083 10.7503 4.75291Z" fill={active ? '#1F72E7' : '#9ca3af'} />
  </svg>
);

const CelebrateIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M18.6188 7.71016C18.885 7.44388 18.885 7.01217 18.6188 6.7459C18.3525 6.47962 17.9208 6.47962 17.6545 6.7459C17.3882 7.01217 17.3882 7.44389 17.6545 7.71016C17.9208 7.97643 18.3525 7.97643 18.6188 7.71016Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path d="M20.1239 12.433C20.4887 12.3396 20.7087 11.9681 20.6152 11.6033C20.5218 11.2385 20.1503 11.0186 19.7855 11.112C19.4207 11.2055 19.2008 11.577 19.2942 11.9418C19.3877 12.3065 19.7592 12.5265 20.1239 12.433Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path d="M21.3468 6.34627C21.6131 6.07999 21.6131 5.64828 21.3468 5.38201C21.0805 5.11573 20.6488 5.11573 20.3825 5.382C20.1162 5.64828 20.1162 6.07999 20.3825 6.34627C20.6488 6.61254 21.0805 6.61254 21.3468 6.34627Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path d="M13.0278 15.693C13.2725 16.2774 13.3641 16.7404 13.3641 17.0006C12.7671 16.9889 10.9969 16.4519 9.27309 14.728C7.54924 13.0042 7.01216 11.234 7.00046 10.637C7.37135 10.637 8.15651 10.8246 9.11664 11.3768C8.92949 11.8856 8.70091 12.3769 8.43285 12.8482C8.68093 13.2108 8.98359 13.5919 9.35009 13.9711C10.4784 12.1854 11.0915 10.1175 11.0915 7.90916C11.0915 6.02594 10.6333 4.0243 9.7668 2.36625C9.59232 2.0324 9.18048 1.90325 8.84663 2.07773C8.51278 2.25221 8.38363 2.66404 8.55811 2.99789C9.32329 4.46196 9.72781 6.24526 9.72781 7.90916C9.72781 8.6563 9.64739 9.38347 9.49338 10.0863C8.03418 9.28599 6.71535 9.1042 6.09151 9.72755C5.97941 9.83965 5.88437 10.0019 5.8371 10.1282C5.83661 10.1282 5.47839 11.1877 4.98176 12.6572C5.01831 12.8765 5.0841 13.1158 5.18255 13.3643C5.5861 14.3844 6.42926 15.5205 7.45518 16.5464L6.54623 17.4554C5.63484 16.544 4.85211 15.5478 4.33062 14.5837C4.07768 15.3324 3.81498 16.1092 3.56155 16.8603C3.99385 17.5202 4.55384 18.1903 5.18304 18.8195L4.27409 19.7285C3.83301 19.2874 3.42167 18.8259 3.05565 18.3585C2.46788 20.0935 2.02242 21.4119 2.02242 21.4119C2.00926 21.4548 2 21.4991 2 21.5464C2 21.6721 2.05069 21.7857 2.13305 21.8676C2.21542 21.9499 2.32898 22.0006 2.45423 22.0006C2.50151 22.0006 2.54586 21.9914 2.58875 21.9782C2.58875 21.9782 13.8724 18.166 13.8724 18.164C13.9982 18.1167 14.1605 18.0217 14.2731 17.9096C14.7775 17.4052 14.7546 16.446 14.3047 15.326C13.8666 15.4079 13.4523 15.5283 13.0278 15.6925V15.693Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path d="M13.1092 10.892C13.2423 11.0251 13.4168 11.0918 13.5913 11.0918C13.7657 11.0918 13.9402 11.0251 14.0733 10.892C16.2538 8.71151 17.4552 5.79555 17.4552 2.6822C17.4552 2.30546 17.1501 2.00037 16.7733 2.00037C16.3966 2.00037 16.0915 2.30546 16.0915 2.6822C16.0915 5.43148 15.0324 8.00433 13.1092 9.92751C12.8431 10.1936 12.8431 10.6254 13.1092 10.892Z" fill={active ? '#1F72E7' : '#9ca3af'} />
    <path d="M21.6345 14.2344C19.9759 13.3679 17.9748 12.9097 16.0916 12.9097C14.9838 12.9097 14.047 13.0096 13.1084 13.2718C12.7843 12.8425 12.4143 12.4146 12.0006 12.0008C11.8256 11.8258 11.6482 11.6596 11.4688 11.5002C11.3338 11.9452 11.172 12.3814 10.9873 12.8084C11.0219 12.842 11.0565 12.8756 11.0911 12.9102C11.3675 13.1866 11.6107 13.4605 11.828 13.7295C11.7145 13.7787 11.5999 13.8294 11.4839 13.884C11.1428 14.0439 10.9961 14.4498 11.1559 14.791C11.3158 15.1322 11.7218 15.2789 12.0625 15.119C13.4388 14.4742 14.5588 14.2744 16.0911 14.2744C17.755 14.2744 19.5383 14.6789 21.0028 15.4441C21.1037 15.4967 21.2114 15.5216 21.3182 15.5216C21.5638 15.5216 21.8012 15.3885 21.923 15.1556C22.0975 14.8217 21.9683 14.4099 21.6345 14.2354V14.2344Z" fill={active ? '#1F72E7' : '#9ca3af'} />
  </svg>
);

const CompareIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M20.5555 6.31024C20.1194 5.89659 19.413 5.89659 18.9769 6.31024L16 9.13378C15.5639 9.54743 15.5639 10.2175 16 10.6311C16.4361 11.0448 17.1425 11.0448 17.5786 10.6311L18.65 9.61492V14.1176C18.65 15.0909 17.8156 15.8823 16.7895 15.8823H8.07001C7.04388 15.8823 6.20946 15.0909 6.20946 14.1176V9.61492L7.28084 10.6311C7.67707 11.0069 8.43557 11.0332 8.85948 10.6311C9.29559 10.2175 9.29559 9.54743 8.85948 9.13378L5.8823 6.31024C5.44619 5.89659 4.73978 5.89659 4.30367 6.31024L1.32708 9.13378C0.890972 9.54743 0.890972 10.2175 1.32708 10.6311C1.7632 11.0448 2.46961 11.0448 2.90572 10.6311L3.9771 9.61492V14.1176C3.9771 16.2587 5.81294 18 8.07031 18H16.7898C19.0471 18 20.883 16.2587 20.883 14.1176V9.61492L21.9544 10.6311C22.3857 11.0402 23.1019 11.04 23.533 10.6311C23.964 10.2223 23.9691 9.54743 23.533 9.13378L20.5555 6.31024Z" fill={active ? '#1F72E7' : '#9ca3af'} />
  </svg>
);

const ExploreIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <line x1="4" y1="14" x2="12" y2="13" stroke={active ? '#1F72E7' : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="13" x2="18" y2="8" stroke={active ? '#1F72E7' : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="13" x2="22" y2="16" stroke={active ? '#1F72E7' : '#9ca3af'} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="4"  cy="14" r="2" fill={active ? '#1F72E7' : '#9ca3af'} />
    <circle cx="12" cy="13" r="2" fill={active ? '#1F72E7' : '#9ca3af'} />
    <circle cx="18" cy="8"  r="2" fill={active ? '#1F72E7' : '#9ca3af'} />
    <circle cx="22" cy="16" r="2" fill={active ? '#1F72E7' : '#9ca3af'} />
  </svg>
);

// ── Deselect button ─────────────────────────────────────────────
const DeselectButton = ({ onClick, ariaLabel }) => (
  <Box
    component="button"
    onClick={onClick}
    aria-label={ariaLabel}
    sx={{
      ml: 'auto', flexShrink: 0,
      background: 'none', border: 'none', cursor: 'pointer',
      color: '#6b7280', p: '2px', lineHeight: 0,
      '&:hover': { color: '#ef4444' },
      '&:focus-visible': { outline: '2px solid #1F72E7', borderRadius: '4px' },
    }}
  >
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  </Box>
);


function StudentEngagement() {
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [transcriptSessionID, setTranscriptSessionID] = useState("");
  const [, setAuthError] = useState(false);
  const [regionList] = useState([]);
  const [tutorList, setTutorList] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [activeVisualization, setActiveVisualization] = useState('transcript');
  const [, setUrlToken] = useState("");
  const [urlTutorId, setUrlTutorId] = useState("");
  const [tutorTalkMoves, setTutorTalkMoves] = useState([]);
  const [studentTalkMoves, setStudentTalkMoves] = useState([]);
  const talkMovesLabels = ['Content Knowledge', 'Learning Community', 'Rigorous Thinking'];
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showGraphs, setShowGraphs] = useState(false);
  const [mathTopicsCovered, setMathTopicsCovered] = useState('');
  const [feedbackSessionHighlights, setFeedbackSessionHighlights] = useState('');
  const [studentParticipation, setStudentParticipation] = useState([]);
  const [analyticstSessionID, setAnalyticsSessionID] = useState('');
  const [tutorName, setTutorName] = useState("");
  const [mostRecentSession, setMostRecentSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, setError] = useState(null);
  const initialSelectionDone = useRef(false);
  const dataFetched = useRef(false);
  const [selectedTutorId, setSelectedTutorId] = useState('');
  const [previousSessionIds, setPreviousSessionIds] = useState([]);
  const [oldestSession, setOldestSession] = useState(null);
  const [inputType] = useState('');
  const [client, setClient] = useState();
  const [analyticsNotFoundError, setAnalyticsNotFoundError] = useState("");
  const [sessionDataCache, setSessionDataCache] = useState({ transcript: null, analytics: null });
  const [dateWasChanged, setDateWasChanged] = useState(false);
  const [selectedConversationStarters, setSelectedConversationStarters] = useState([]);
  const [selectedTreeForCelebrate, setSelectedTreeForCelebrate] = useState(null);
  const [selectedTreesForCompare, setSelectedTreesForCompare] = useState(new Set());
  const [celebrateTreeImage, setCelebrateTreeImage] = useState(null);
  const [compareTreeImages, setCompareTreeImages] = useState([]);
  const [selectedCompareConversationStarters, setSelectedCompareConversationStarters] = useState([]);
  const [selectedFeedbackTree, setSelectedFeedbackTree] = useState(null);
  const [feedbackTreeImage, setFeedbackTreeImage] = useState(null);
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [leftTree, setLeftTree] = useState(null);
  const [rightTree, setRightTree] = useState(null);

  const { width: screenWidth } = useWindowSize();

  const muiTheme = createTheme({
    typography: { fontFamily: '"DM Sans", "Mulish", "Segoe UI", sans-serif' },
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const calculateSessionDuration = (metrics, sessionDiscourseHighlights) => {
    if (metrics.sessionTime && metrics.sessionTime > 0) return Math.round(metrics.sessionTime / 60);
    if (sessionDiscourseHighlights.length > 0)
      return Math.round(Math.max(...sessionDiscourseHighlights.map(h => h.end)) / 60);
    return 0;
  };

  const {
    isConverting,
    errors,
    convertTreeToSvg,
    clearSvgData
  } = useTreeToSvgConverter({
    containerSelector: '.tree-container',
    options: { quality: 1, bgcolor: 'transparent', cacheBust: true }
  });

  const clearTreeSelections = () => {
    setLeftTree(null);
    setRightTree(null);
    setSelectedTreeForCelebrate(null);
    setCelebrateTreeImage(null);
    setSelectedConversationStarters([]);
    setSelectedTreesForCompare(new Set());
    setCompareTreeImages([]);
    setSelectedCompareConversationStarters([]);
    clearSvgData();
  };

  // ── Shared layout sub-components  ──────────────────────────────
  const TabContentWrapper = ({ children }) => (
    <Box sx={{ padding: isMobile ? '16px' : TAB_CONTENT_PADDING, textAlign: 'left' }}>
      {children}
    </Box>
  );

  const LeftPanel = ({ children }) => (
    <Grid item xs={12} md={6} sx={{ border: LEFT_PANEL_BORDER, borderRadius: LEFT_PANEL_RADIUS, backgroundColor: LEFT_PANEL_BG, overflow: 'hidden' }}>
      {children}
    </Grid>
  );

  const RightPanel = ({ children }) => (
    <Grid item xs={12} md={6}>
      <Box sx={{ p: isMobile ? 1 : 2, height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
        {children}
      </Box>
    </Grid>
  );

  const GenerateButton = ({ onClick, disabled, label }) => (
    <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'center' }}>
      <Button
        variant="contained"
        onClick={onClick}
        disabled={disabled}
        sx={{
          backgroundColor: PRESENTATION_RESOURCES.styles.buttonPrimary,
          color: '#fff',
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '20px',
          fontSize: isMobile ? '13px' : '14px',
          px: 3,
          minHeight: '44px',
          width: isMobile ? '100%' : 'auto',
          '&:hover': { backgroundColor: '#1558c0' },
          '&.Mui-disabled': { backgroundColor: '#B1AFAD', color: '#fff' },
          '&:focus-visible': { outline: '3px solid #1F72E7', outlineOffset: '2px' },
        }}
      >
        {label}
      </Button>
    </Box>
  );

  // ── Tab definitions  ──
  const TABS = [
    { label: 'Introduce Talk Trees', Icon: IntroIcon },
    { label: 'Celebrate',            Icon: CelebrateIcon },
    { label: 'Compare',              Icon: CompareIcon },
    ...(client !== 'teachfx' ? [{ label: 'Explore My Talk', Icon: ExploreIcon }] : []),
  ];

  // ── Tree selection handlers  ───────────────────────
  const handleCelebrateTreeSelection = async (sessionId) => {
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
    } else {
      setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      const newCompareSet = new Set(rightTree ? [sessionId, rightTree] : [sessionId]);
      setSelectedTreesForCompare(newCompareSet);
      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        const svgDataUrl = await convertTreeToSvg(sessionId);
        setCelebrateTreeImage(svgDataUrl);
        setCompareTreeImages(prev => {
          const filtered = prev.filter(img => img.sessionId !== sessionId);
          return [{ sessionId, image: svgDataUrl }, ...filtered];
        });
      } catch (error) {
        console.error('Error converting celebrate tree to SVG:', error);
        alert('Failed to capture tree image. Please try again.');
      }
    }
  };

  const handleCompareTreeSelection = async (sessionId) => {
    if (leftTree === sessionId) {
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
      setSelectedTreesForCompare(new Set(rightTree ? [rightTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === rightTree));
      clearSvgData(sessionId);
      if (selectedFeedbackTree === sessionId) { setSelectedFeedbackTree(null); setFeedbackTreeImage(null); }
    } else if (rightTree === sessionId) {
      setRightTree(null);
      setSelectedTreesForCompare(new Set(leftTree ? [leftTree] : []));
      setCompareTreeImages(prev => prev.filter(img => img.sessionId === leftTree));
      clearSvgData(sessionId);
    } else {
      if (!leftTree) {
        setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
        setSelectedTreesForCompare(new Set([sessionId])); setSelectedFeedbackTree(sessionId);
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCelebrateTreeImage(svgDataUrl); setFeedbackTreeImage(svgDataUrl);
          setCompareTreeImages([{ sessionId, image: svgDataUrl }]);
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      } else if (!rightTree) {
        setRightTree(sessionId); setSelectedTreesForCompare(new Set([leftTree, sessionId]));
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCompareTreeImages(prev => [...prev, { sessionId, image: svgDataUrl }]);
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      } else {
        const oldRight = rightTree; setRightTree(sessionId);
        setSelectedTreesForCompare(new Set([leftTree, sessionId])); clearSvgData(oldRight);
        try {
          await new Promise(resolve => setTimeout(resolve, 200));
          const svgDataUrl = await convertTreeToSvg(sessionId);
          setCompareTreeImages(prev => {
            const filtered = prev.filter(img => img.sessionId !== oldRight);
            return [...filtered, { sessionId, image: svgDataUrl }];
          });
        } catch (error) { console.error('Error converting tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
      }
    }
  };

  const handleFeedbackTreeSelection = async (sessionId) => {
    if (!sessionId) { console.warn('sessionId was undefined or null'); return; }
    if (selectedFeedbackTree === sessionId) {
      setSelectedFeedbackTree(null); setFeedbackTreeImage(null); clearSvgData(sessionId);
      setLeftTree(null); setSelectedTreeForCelebrate(null); setCelebrateTreeImage(null);
    } else {
      setSelectedFeedbackTree(sessionId); setLeftTree(sessionId); setSelectedTreeForCelebrate(sessionId);
      try {
        await new Promise(resolve => setTimeout(resolve, 200));
        const svgDataUrl = await convertTreeToSvg(sessionId);
        setFeedbackTreeImage(svgDataUrl); setCelebrateTreeImage(svgDataUrl);
        setCompareTreeImages(prev => {
          const filtered = prev.filter(img => img.sessionId !== sessionId);
          return [{ sessionId, image: svgDataUrl }, ...filtered];
        });
      } catch (error) { console.error('Error converting feedback tree to SVG:', error); alert('Failed to capture tree image. Please try again.'); }
    }
  };

  const onTreeSelect = (sessionId) => {
    if (mainTabIndex === 0) { setMainTabIndex(1); handleCelebrateTreeSelection(sessionId); }
    else if (mainTabIndex === 1) handleCelebrateTreeSelection(sessionId);
    else if (mainTabIndex === 2) handleCompareTreeSelection(sessionId);
    else if (mainTabIndex === 3) handleFeedbackTreeSelection(sessionId);
  };

  // ── Data helpers  ──────────────────────────────────
  const getDayKey = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };

  const PERIOD_PATTERNS = {
    anyInBrackets: { pattern: /\[([a-zA-Z0-9]+)\]/i, valueGroup: 1, displayGroup: 0, priority: 100 },
    classInBrackets: { pattern: /\[(Class\s+(\d+))\]/i, valueGroup: 2, displayGroup: 1, priority: 90 },
    ClassNStar: { pattern: /Class\s+(\d+)/i, valueGroup: 1, displayGroup: 0, priority: 80 },
    periodXX: { pattern: /\(Period\s+([a-zA-Z0-9]+)\)/i, valueGroup: 1, displayGroup: 0, priority: 70 },
    NNN: { pattern: /(\d\d\d)/i, valueGroup: 1, displayGroup: 0, priority: 50 }
  };

  const extractPeriodIdentifier = (sessionTitle) => {
    if (!sessionTitle) return null;
    for (const [key, config] of Object.entries(PERIOD_PATTERNS)) {
      const match = sessionTitle.match(config.pattern);
      if (match) {
        const value = match[config.valueGroup]?.trim();
        const display = config.displayGroup === 0 ? match[0] : match[config.displayGroup];
        if (value) return { type: key, value, display, priority: config.priority };
      }
    }
    return null;
  };

  const extractFeedbackContent = (infoBoxes, panelName) => {
    if (!infoBoxes || infoBoxes.length === 0) return null;
    for (const box of infoBoxes) {
      if (box.boxContent && Array.isArray(box.boxContent)) {
        const panel = box.boxContent.find(content => content.panel === panelName);
        if (panel && panel.infoText) return panel.infoText.join('');
      }
    }
    return null;
  };

  const formatDateToYYYYMMDD = (date, region) => {
    if (!date) return "";
    try {
      const timezone = getTimeZoneForRegion(region);
      const options = { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' };
      const formattedDate = new Intl.DateTimeFormat('en-US', options)
        .format(date).split('/').map(part => part.padStart(2, '0'));
      return `${formattedDate[2]}-${formattedDate[0]}-${formattedDate[1]}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const latestSessionIdRef = useRef(transcriptSessionID);

  const handleTalkmoves = async () => {
    if (transcriptSessionID === "") { setAnalyticsNotFoundError(""); return; }
    if (sessionDataCache.analytics && sessionDataCache.analytics.sessionId === transcriptSessionID)
      return sessionDataCache.analytics.data;
    const currentFetchSessionId = transcriptSessionID;
    setAnalyticsLoading(true); setStudentParticipation([]);
    try {
      const sessionData = data.find(item => item.sessionId === currentFetchSessionId);
      if (!sessionData) throw new Error("Session not found in existing data");
      setAuthError(false); setAnalyticsNotFoundError(""); setAnalyticsSessionID(sessionData.sessionId);
      const extractCountsInOrder = (data, desiredOrder) =>
        desiredOrder.map(label => { const cat = data.find(item => item.label === label); return cat ? cat.count : 0; });
      if (sessionData.metrics?.tutorTalkmoveCategories) setTutorTalkMoves(extractCountsInOrder(sessionData.metrics.tutorTalkmoveCategories, talkMovesLabels));
      else setTutorTalkMoves([0, 0, 0]);
      if (sessionData.metrics?.studentTalkmoveCategories) setStudentTalkMoves(extractCountsInOrder(sessionData.metrics.studentTalkmoveCategories, talkMovesLabels));
      else setStudentTalkMoves([0, 0, 0]);
      setMathTopicsCovered(sessionData.sessionMetadata?.content?.title || "No topic information available");
      const generateFeedbackHighlights = (metrics) => {
        if (!metrics) return "No feedback data available.";
        const tutorMoveCount = metrics.tutorTalkMoves?.length || 0;
        const studentMoveCount = metrics.studentTalkMoves?.length || 0;
        const participationRatio = metrics.tutorTalkTime && metrics.tutorTalkTime > 0
          ? (metrics.studentTalkTime / metrics.tutorTalkTime).toFixed(2) : "N/A";
        return `This session had ${tutorMoveCount} tutor talk moves and ${studentMoveCount} student talk moves. The ratio of student to tutor participation was ${participationRatio}.`;
      };
      setFeedbackSessionHighlights(generateFeedbackHighlights(sessionData.metrics));
      if (sessionData.participantContributions && Array.isArray(sessionData.participantContributions))
        setStudentParticipation(sessionData.participantContributions.filter(e => e.participantType === "student"));
      else setStudentParticipation([]);
      setSessionDataCache(prev => ({ ...prev, analytics: { sessionId: transcriptSessionID, data: [sessionData] } }));
      setAnalyticsLoading(false);
      return [sessionData];
    } catch (error) {
      console.error("Error fetching talk moves:", error);
      setAnalyticsLoading(false);
      setAnalyticsNotFoundError("The data could not be retrieved. Error: " + error.message);
      return null;
    }
  };

  const handleDataRetrievalClick = async () => {
    if (!analyticstSessionID) { setShowGraphs(false); return; }
    setActiveVisualization('talkTree'); setAnalyticsLoading(true); setShowGraphs(false);
    try {
      const analyticsData = await handleTalkmoves();
      if (!analyticsData) throw new Error("Failed to retrieve analytics data");
      const currentSessionData = await processTranscriptData(analyticstSessionID);
      if (!currentSessionData) throw new Error("Failed to process transcript data for current session");
      if (previousSessionIds.length > 0)
        for (const id of previousSessionIds) if (id && id !== analyticstSessionID) await processTranscriptData(id);
      setAnalyticsLoading(false); setActiveVisualization('talkTree'); setShowGraphs(true);
    } catch (error) {
      console.error("Error retrieving data:", error); setAnalyticsLoading(false); setShowGraphs(false);
    }
  };

  const handleButtonClick = async () => {
    setActiveVisualization("talkTree");
    if (selectedPeriod) { setShowGraphs(true); await handleDataRetrievalClick(); }
    else setShowGraphs(false);
  };

  const extractQueryParams = useCallback(async () => {
    if (dataFetched.current) return;
    dataFetched.current = true;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const tutorId = urlParams.get("tutorId");
    const clientParam = urlParams.get("client");
    const dateRangeLimit = urlParams.get("prior");
    if (!token || !tutorId) { setError("Missing URL parameters: token or tutorId"); return null; }
    setClient(clientParam ? clientParam.toLowerCase() : "generic");
    setIsLoading(true); setError(null);
    mixpanel.identify(tutorId);
    try {
      var apiURL = `${API_BASE_URL}v1aux/analysis/tutorData?tutorId=${tutorId}&token=${token}`;
      if (dateRangeLimit) apiURL += `&dateRangeLimit=${dateRangeLimit}`;
      const response = await axios.get(apiURL);
      const tutorDataArray = response.data;
      if (!tutorDataArray || tutorDataArray.length === 0) { setError("No tutor data available."); setIsLoading(false); return null; }
      const tutor = tutorDataArray[0]?.sessionMetadata.participants.find(p => p.type === "tutor");
      setTutorName(tutor ? tutor.name : "Unknown Tutor");
      const processedData = tutorDataArray.map(session => ({
        sessionId: session.sessionId, tutorId: session.tutorId, tutorName: tutorName,
        region: session.sessionMetadata.region || "", sessionTitle: session.sessionMetadata.title || "Untitled Session",
        startTime: session.sessionMetadata?.startTime || null, lessonSummary: session.sessionMetadata?.lessonSummary || "",
        subject: session.sessionMetadata?.subject || "", grade: session.sessionMetadata?.grade || "",
        endTime: session.sessionMetadata?.endTime || null, metrics: session.metrics || {},
        participants: session.sessionMetadata?.participants || [], participantContributions: session.participantContributions || [],
        sessionMetadata: session.sessionMetadata || {}, sessionDiscourseHighlights: session.sessionDiscourseHighlights || [],
        infoBoxes: session.infoBoxes || [], strengthsOpportunitiesFeedback: session.strengths_opportunities_feedback || []
      }));
      const regionFromData = processedData[0]?.region || processedData[0]?.sessionMetadata?.region || "";
      if (regionFromData) setSelectedRegion(regionFromData);
      var mostRecent = null;
      if (processedData.length > 0) {
        const sortedData = [...processedData].sort((a, b) => b.startTime - a.startTime);
        mostRecent = sortedData[0]; setMostRecentSession(mostRecent);
        const oldest = sortedData[sortedData.length - 1]; setOldestSession(oldest);
        setSelectedTutorId(tutorId);
        if (mostRecent.startTime) {
          const sessionDate = new Date(mostRecent.startTime);
          setSelectedDate(new Intl.DateTimeFormat('en-CA').format(sessionDate));
        }
        setAnalyticsSessionID(mostRecent.sessionId); setTranscriptSessionID(mostRecent.sessionId);
      }
      setUrlToken(token); setUrlTutorId(tutorId); setData(processedData); setIsLoading(false);
      handleButtonClick();
      mixpanel.track("tutorFeedbackPageLoaded", {
        tutorId, tutorRegion: regionFromData, sessionCount: processedData.length,
        viewedSessionId: mostRecent ? mostRecent.sessionId : null,
      });
      return { tutorId, tutorName, token, sessions: processedData, mostRecentSession };
    } catch (error) {
      console.error("Error fetching tutor data:", error);
      setError("Failed to fetch tutor data. Please check your credentials.");
      dataFetched.current = false; setIsLoading(false); return null;
    }
  }, []);

  const apiCache = new Map();

  const processTranscriptData = useCallback(async (transcriptSessionID) => {
    if (!transcriptSessionID) { setError("Session ID is required"); return null; }
    const cacheKey = `${transcriptSessionID}`;
    if (apiCache.has(cacheKey)) return apiCache.get(cacheKey);
    setIsLoading(true); setError(null);
    try {
      const sessionData = data.find(item => item.sessionId === transcriptSessionID);
      if (!sessionData) throw new Error(`Session ${transcriptSessionID} not found in existing data`);
      const metrics = sessionData.metrics || {};
      const participantContributions = sessionData.participantContributions || [];
      const sessionMetadata = sessionData.sessionMetadata || {};
      const sessionDiscourseHighlights = sessionData.sessionDiscourseHighlights || [];
      const processedData = sessionDiscourseHighlights.map(highlight => ({
        category: highlight.category, end: highlight.end, noticingScore: highlight.noticingScore,
        participantId: highlight.participantId, segmentId: highlight.segmentId, speakerType: highlight.speakerType,
        start: highlight.start, text: highlight.text, type: highlight.type,
        statementId: highlight.segmentId, talkMoveCategory: highlight.category || '', talkMoveType: highlight.type || ''
      }));
      const participants = Array.isArray(sessionMetadata.participants) ? sessionMetadata.participants : [];
      const activeParticipants = participants
        .filter(p => p.type === 'student' && participantContributions.some(c => c.participantId === p.id))
        .map(p => ({ typeof: p.type, id: p.id, name: p.name }));
      const startTime = sessionMetadata.startTime || Date.now();
      const sessionDurationMinutes = calculateSessionDuration(metrics, sessionDiscourseHighlights);
      const realEndTime = startTime + (sessionDurationMinutes * 60 * 1000);
      const sessionMeta = {
        activeParticipants, participants, siteDirectors: sessionMetadata.siteDirectors || [],
        sessionId: sessionMetadata.sessionId || transcriptSessionID, title: sessionMetadata.title || "Untitled Session",
        tutorId: sessionMetadata.tutorId || '', startTime, endTime: realEndTime, region: sessionMetadata.region || '',
        lessonSummary: sessionMetadata?.lessonSummary || "", subject: sessionMetadata?.subject || "", grade: sessionMetadata?.grade || "",
        feedbackSummary: extractFeedbackContent(sessionData.infoBoxes, 'feedbackSummary') || 'No summary available.',
        feedbackMathTopics: extractFeedbackContent(sessionData.infoBoxes, 'feedbackMathTopics') || 'No math topics available.'
      };
      setSelectedRegion(sessionMeta.region);
      const enrichedData = {
        sessionMetadata: sessionMeta, processedData,
        metrics: {
          tutorMetrics: { talkMoves: metrics.tutorTalkMoves || [], talkMovesCategories: metrics.tutorTalkmoveCategories || [], utteranceCount: metrics.tutorUtteranceCount || 0, talkTime: metrics.tutorTalkTime || 0, mathTermCount: metrics.tutorMathTermCount || 0 },
          studentMetrics: { talkMoves: metrics.studentTalkMoves || [], talkMovesCategories: metrics.studentTalkmoveCategories || [], utteranceCount: metrics.studentUtteranceCount || 0, talkTime: metrics.studentTalkTime || 0, mathTermCount: metrics.studentMathTermCount || 0 },
          sessionMetrics: { sessionTime: metrics.sessionTime || 0, sessionTimeMinutes: sessionDurationMinutes, totalUtterances: metrics.totalUtterances || 0, totalMathTermCount: metrics.totalMathTermCount || 0, longWaitTimes: metrics.longWaitTimes || 0, shortWaitTimes: metrics.shortWaitTimes || 0 }
        }
      };
      apiCache.set(cacheKey, enrichedData); setIsLoading(false); return enrichedData;
    } catch (error) {
      console.error("Error processing transcript data:", error);
      setError(`The data could not be retrieved. Error: ${error.message || 'unknown error'}`);
      setIsLoading(false); return null;
    }
  }, [data]);

  const fetchCurrentAndPreviousSessions = async (selectedPeriodId, tutorId) => {
    if (!selectedPeriodId || !tutorId) return { currentSession: null, previousSessions: [] };
    try {
      const selectedPeriod = availablePeriods.find(p => p.id === selectedPeriodId);
      if (!selectedPeriod) return { currentSession: null, previousSessions: [] };
      const sessionList = data.map(session => ({
        sessionId: session.sessionId, startTime: session.sessionMetadata?.startTime || null,
        endTime: session.sessionMetadata?.endTime || null,
        title: session.sessionTitle || session.sessionMetadata?.title || 'Untitled Session'
      })).filter(s => s.startTime !== null);
      const selectedDate = new Date(selectedPeriod.startTime);
      const selectedSessionId = selectedPeriod.sessionId;
      const selectedSessionTitle = selectedPeriod.title;
      const selectedPeriodIdentifier = extractPeriodIdentifier(selectedSessionTitle);
      const usePatternMatching = !!selectedPeriodIdentifier;
      const endTime = selectedPeriod.startTime;
      const numPriorDays = 180;
      const numSessionsDisplayed = 6;
      const nDaysAgo = new Date(selectedDate);
      nDaysAgo.setDate(nDaysAgo.getDate() - numPriorDays);
      const startTime = nDaysAgo.getTime();
      const filteredSessions = sessionList.filter(s => s.startTime >= startTime && s.startTime <= endTime);
      const sortedSessions = filteredSessions.sort((a, b) => b.startTime - a.startTime);
      const sessionsByDay = {};
      const sessionTimeWindow = 60;
      sortedSessions.forEach(session => {
        if (session.sessionId === selectedSessionId) return;
        const sessionDate = new Date(session.startTime);
        const dayKey = getDayKey(session.startTime);
        if (sessionDate.getTime() > selectedDate.getTime()) return;
        if (!sessionsByDay[dayKey]) sessionsByDay[dayKey] = [];
        const sessionPeriodIdentifier = extractPeriodIdentifier(session.title);
        let matchScore = 0; let exactClassMatch = false;
        if (usePatternMatching && sessionPeriodIdentifier && selectedPeriodIdentifier) {
          if (sessionPeriodIdentifier.value === selectedPeriodIdentifier.value) { matchScore = sessionPeriodIdentifier.priority; exactClassMatch = true; }
        } else if (!usePatternMatching) {
          const sessionTime = new Date(session.startTime); const selectedTime = new Date(selectedPeriod.startTime);
          const sessionTimeMinutes = (sessionTime.getHours() * 60) + sessionTime.getMinutes();
          const selectedTimeMinutes = (selectedTime.getHours() * 60) + selectedTime.getMinutes();
          let timeDiff = Math.abs(sessionTimeMinutes - selectedTimeMinutes);
          if (timeDiff > 720) timeDiff = 1440 - timeDiff;
          if (timeDiff <= sessionTimeWindow && (!usePatternMatching || !sessionPeriodIdentifier))
            matchScore = 50 - Math.floor((timeDiff / sessionTimeWindow) * 50);
        }
        if (matchScore > 0) sessionsByDay[dayKey].push({ ...session, matchScore, exactClassMatch, periodIdentifier: sessionPeriodIdentifier?.display || null, classNumber: sessionPeriodIdentifier?.value || null });
      });
      const weekDates = [];
      for (let i = 0; i < numPriorDays; i++) {
        const date = new Date(selectedDate); date.setDate(date.getDate() - i);
        weekDates.push({ date, dayKey: getDayKey(date.getTime()) });
      }
      const priorSessions = [];
      priorSessions.push({ sessionId: selectedPeriod.sessionId, title: selectedPeriod.title, startTime: selectedPeriod.startTime, students: selectedPeriod.students || [], dayKey: weekDates[0].dayKey, date: weekDates[0].date, periodIdentifier: selectedPeriodIdentifier ? selectedPeriodIdentifier.display : null, classNumber: selectedPeriodIdentifier ? selectedPeriodIdentifier.value : null });
      const findBestMatchForDay = (dayKey, targetClassNumber) => {
        if (!sessionsByDay[dayKey] || sessionsByDay[dayKey].length === 0) return null;
        if (targetClassNumber) {
          const exactMatches = sessionsByDay[dayKey].filter(s => s.exactClassMatch && s.classNumber === targetClassNumber);
          if (exactMatches.length > 0) return exactMatches.sort((a, b) => b.startTime - a.startTime)[0];
        }
        if (!targetClassNumber) return sessionsByDay[dayKey].sort((a, b) => b.matchScore - a.matchScore)[0];
        return null;
      };
      const targetClassNumber = selectedPeriodIdentifier ? selectedPeriodIdentifier.value : null;
      for (let i = 1; i < numPriorDays && priorSessions.length < numSessionsDisplayed + 1; i++) {
        const dayKey = weekDates[i].dayKey; const dayDate = weekDates[i].date;
        const bestMatch = findBestMatchForDay(dayKey, targetClassNumber);
        if (bestMatch) priorSessions.push({ sessionId: bestMatch.sessionId, title: bestMatch.title || 'No Title', startTime: bestMatch.startTime, dayKey, date: dayDate, matchScore: bestMatch.matchScore, periodIdentifier: bestMatch.periodIdentifier, classNumber: bestMatch.classNumber, exactClassMatch: bestMatch.exactClassMatch });
      }
      return { currentSession: priorSessions[0], previousSessions: priorSessions.slice(1, numSessionsDisplayed + 1) };
    } catch (error) {
      console.error('Error fetching current and previous sessions:', error);
      return { currentSession: null, previousSessions: [] };
    }
  };

  const fetchAvailablePeriods = async () => {
    setAvailablePeriods([]);
    try {
      if (!selectedDate || !selectedRegion || !selectedTutorId) return;
      let sessionsForDate = [];
      if (data && Array.isArray(data)) {
        sessionsForDate = data.filter(session => {
          const sessionStartTime = session.startTime;
          if (!sessionStartTime) return false;
          const sessionDate = new Date(sessionStartTime);
          return formatDateToYYYYMMDD(sessionDate, selectedRegion) === selectedDate;
        });
      }
      if (sessionsForDate.length > 0) {
        const periods = sessionsForDate.map(session => {
          const startTime = session.startTime ? new Date(session.startTime) : null;
          if (!startTime || isNaN(startTime.getTime())) return null;
          const sessionTitle = session.sessionTitle || "No Title Available";
          const studentParticipants = (session.sessionMetadata?.participants || [])
            .filter(p => p.type === "student").map(p => ({ id: p.id, name: p.name || p.id }));
          return { id: `${session.sessionId}_${startTime.getTime()}`, label: formatTimestampInRegionTimezone(startTime.getTime(), selectedRegion), sessionId: session.sessionId, startTime: startTime.getTime(), title: sessionTitle, studentDetails: studentParticipants };
        });
        const sortedPeriods = periods.filter(Boolean).sort((a, b) => a.startTime - b.startTime);
        setAvailablePeriods(sortedPeriods);
        if (!dateWasChanged && !initialSelectionDone.current && sortedPeriods.length > 0) setDateWasChanged(false);
        else if (selectedPeriod === "" && !initialSelectionDone.current) {
          const mostRecentPeriod = sortedPeriods[sortedPeriods.length - 1];
          setSelectedPeriod(mostRecentPeriod.id); setTranscriptSessionID(mostRecentPeriod.sessionId);
          setAnalyticsSessionID(mostRecentPeriod.sessionId); setActiveVisualization('talkTree');
          setTimeout(() => { setShowGraphs(true); handleDataRetrievalClick(); }, 300);
        }
      } else {
        setSelectedPeriod(""); setTranscriptSessionID(""); setAnalyticsSessionID(""); setShowGraphs(false); setDateWasChanged(false);
      }
    } catch (error) {
      console.error("Error in fetchAvailablePeriods:", error);
      setSelectedPeriod(""); setTranscriptSessionID(""); setAnalyticsSessionID(""); setShowGraphs(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate === selectedDate) return;
    const hasSelections = selectedTreeForCelebrate || selectedTreesForCompare.size > 0 || selectedConversationStarters.length > 0 || selectedCompareConversationStarters.length > 0;
    if (hasSelections) {
      const confirmed = window.confirm('Your tree and conversation starters selections will be cleared before your new trees are displayed. To continue, choose "OK"');
      if (!confirmed) return;
      clearTreeSelections();
    }
    setSelectedFeedbackTree(null); setFeedbackTreeImage(null); setDateWasChanged(true);
    setSelectedDate(newDate); setSelectedPeriod(''); setTranscriptSessionID('');
    setAnalyticsSessionID(''); setShowGraphs(false); setPreviousSessionIds([]); setAvailablePeriods([]);
    initialSelectionDone.current = true; apiCache.clear();
    setSessionDataCache({ transcript: null, analytics: null });
    if (selectedTutorId && selectedRegion) setTimeout(() => fetchAvailablePeriods(), 200);
  };

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    const hasSelections = selectedTreeForCelebrate || selectedTreesForCompare.size > 0 || selectedConversationStarters.length > 0 || selectedCompareConversationStarters.length > 0;
    if (hasSelections) {
      const confirmed = window.confirm('Your tree and conversation starters selections will be cleared before your new trees are displayed. Choose "OK" to continue.');
      if (!confirmed) return;
      clearTreeSelections();
    }
    setSelectedFeedbackTree(null); setFeedbackTreeImage(null); setSelectedPeriod(newPeriod);
    if (newPeriod === '') { setAnalyticsSessionID(''); setTranscriptSessionID(''); setPreviousSessionIds([]); setShowGraphs(false); return; }
    setAnalyticsLoading(true);
    const selectedPeriodObj = availablePeriods.find(p => p.id === newPeriod);
    if (selectedPeriodObj) {
      const sessionId = selectedPeriodObj.sessionId;
      setAnalyticsSessionID(sessionId); setTranscriptSessionID(sessionId);
      fetchCurrentAndPreviousSessions(newPeriod, selectedTutorId, 180).then(result => {
        if (result.currentSession) {
          const prevIds = result.previousSessions.filter(s => s && s.sessionId).map(s => s.sessionId);
          setPreviousSessionIds(prevIds);
          processTranscriptData(sessionId).then(() => {
            const processSeq = async (ids) => { for (const id of ids) if (id && id !== sessionId) await processTranscriptData(id); };
            return processSeq(prevIds);
          }).then(() => { setActiveVisualization('talkTree'); setShowGraphs(true); return handleTalkmoves(); })
            .then(() => setAnalyticsLoading(false))
            .catch(err => { console.error("Error processing session data:", err); setShowGraphs(false); setAnalyticsLoading(false); });
        } else { setShowGraphs(false); setPreviousSessionIds([]); setAnalyticsLoading(false); }
      }).catch(err => { console.error("Error in session fetch:", err); setPreviousSessionIds([]); setShowGraphs(false); setAnalyticsLoading(false); });
    } else { setAnalyticsSessionID(''); setTranscriptSessionID(''); setPreviousSessionIds([]); setShowGraphs(false); setAnalyticsLoading(false); }
  };

  const generateIntroductionPresentation = () => {
    sessionStorage.setItem('presentationData', JSON.stringify({ mode: 'introduction', tutorName, introContent: PRESENTATION_RESOURCES.tabs.introduction.content, client }));
    window.open('/presentation', '_blank');
  };

  const generateCelebratePresentation = async () => {
  const sessionData = data.find(item => item.sessionId === selectedTreeForCelebrate);
  const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;
  
  sessionStorage.setItem('presentationData', JSON.stringify({ 
    mode: 'celebrate', 
    selectedTree: { sessionId: selectedTreeForCelebrate, startTime: sessionData?.startTime, image: celebrateTreeImage }, 
    selectedStarters: selectedConversationStarters, 
    tutorName, 
    client,
    treeScale: scale  
  }));
  window.open('/presentation', '_blank');
};

  const generateComparePresentation = async () => {
  const selectedTreeData = await Promise.all(Array.from(selectedTreesForCompare).map(async (sessionId) => {
    const sessionData = data.find(item => item.sessionId === sessionId);
    return { sessionId, startTime: sessionData?.startTime, image: compareTreeImages.find(img => img.sessionId === sessionId)?.image };
  }));
  
  const scale = screenWidth > 1440 ? 0.8 : screenWidth > 1024 ? 0.65 : screenWidth > 768 ? 0.5 : 0.6;
  
  sessionStorage.setItem('presentationData', JSON.stringify({ 
    mode: 'compare', 
    selectedTrees: selectedTreeData, 
    selectedStarters: selectedCompareConversationStarters, 
    tutorName, 
    client,
    treeScale: scale  
  }));
  window.open('/presentation', '_blank');
};

  const renderIntroductionTab = () => (
    <TabContentWrapper>
      <Grid container spacing={isMobile ? 2 : 3} alignItems="flex-start">
        <LeftPanel>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: isMobile ? '200px' : '300px' }}>
            <Typography variant="h6" gutterBottom>Sample Talk Tree</Typography>
            <img
              src="/Sample Tree.png"
              alt="Sample talk tree showing branches, leaves, and colored flowers representing different types of student talk moves"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: isMobile ? '250px' : '400px' }}
            />
          </Box>
          <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
        </LeftPanel>

        <RightPanel>
          {PRESENTATION_RESOURCES.tabs.introduction.pages.map((page, pageIndex) => (
            <Box key={pageIndex} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom><b>{page.content.heading}</b></Typography>
              {page.content.description.length > 0 && (
                <Typography variant="body1" paragraph>{page.content.description}</Typography>
              )}
              <Typography variant="body1" component="div">
                <ul style={{ paddingLeft: '1.5em', margin: 0 }}>
                  {page.content.pointList.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              </Typography>
            </Box>
          ))}
          <GenerateButton onClick={generateIntroductionPresentation} label={PRESENTATION_RESOURCES.messages.generatePresentation} />
        </RightPanel>
      </Grid>
    </TabContentWrapper>
  );

  const renderCelebrateTab = () => {
    const handleConversationStarterChange = (starter, checked) =>
      setSelectedConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    const handleCelebrateGeneratePresentation = () => {
      if (!selectedTreeForCelebrate) { alert(PRESENTATION_RESOURCES.messages.selectTree); return; }
      if (selectedConversationStarters.length === 0) {
        const confirmProceed = window.confirm(PRESENTATION_RESOURCES.messages.proceedWithoutStarters);
        if (!confirmProceed) return;
      }
      generateCelebratePresentation();
    };

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting tree to image...</Typography>
          </Box>
        )}
        {errors.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.errorBackground, borderRadius: 1 }} role="alert">
            <Typography variant="body2" color="error">Error: {errors[errors.length - 1].error.message}</Typography>
          </Box>
        )}

        <Grid container spacing={isMobile ? 2 : 3} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, minHeight: isMobile ? '200px' : '300px' }}>
              {leftTree && celebrateTreeImage ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%', justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {formatDisplayDate(new Date(data.find(item => item.sessionId === leftTree)?.startTime))}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1F72E7', px: '8px', py: '2px', whiteSpace: 'nowrap' }}>
                      Selected
                    </Typography>
                    <DeselectButton onClick={() => handleCelebrateTreeSelection(leftTree)} ariaLabel="Deselect this tree" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <img
                      src={celebrateTreeImage}
                      alt={`Talk tree for session on ${formatDisplayDate(new Date(data.find(item => item.sessionId === leftTree)?.startTime))}`}
                      style={{ display: 'block', width: getCompareImageStyle(screenWidth), height: 'auto', objectFit: 'contain' }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', border: LEFT_PANEL_BORDER, borderRadius: 1, p: 2, minHeight: isMobile ? '150px' : '300px', width: '90%' }}>
                  <Typography variant="subtitle1" color="text.secondary">Click a tree to see it here</Typography>
                </Box>
              )}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6" gutterBottom><b>{PRESENTATION_RESOURCES.tabs.celebrate.title}</b></Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.celebrate.description}</Typography>
            <Typography variant="h6" gutterBottom><b>{PRESENTATION_RESOURCES.tabs.celebrate.conversationStarterTitle}</b></Typography>
            <Box role="group" aria-label="Conversation starters">
              {PRESENTATION_RESOURCES.tabs.celebrate.conversationStarters.map((starter, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={selectedConversationStarters.includes(starter)}
                      onChange={(e) => handleConversationStarterChange(starter, e.target.checked)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleConversationStarterChange(starter, !selectedConversationStarters.includes(starter)); } }}
                      inputProps={{ 'aria-label': starter }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={handleCelebrateGeneratePresentation}
              disabled={!selectedTreeForCelebrate || isConverting}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  const renderCompareTab = () => {
    const handleStarterChange = (starter, checked) =>
      setSelectedCompareConversationStarters(prev => checked ? [...prev, starter] : prev.filter(s => s !== starter));

    const imageStyle = {
      display: 'block', objectFit: 'contain', objectPosition: 'center',
      maxHeight: '100%', transition: 'transform 0.2s ease', ...getCompareImageStyle(screenWidth),
    };

    return (
      <TabContentWrapper>
        {isConverting && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: PRESENTATION_RESOURCES.styles.loadingBackground, borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="body2">Converting trees to images...</Typography>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          <LeftPanel>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', boxSizing: 'border-box', overflow: 'hidden' }}>
              {[{ key: 'left', tree: leftTree }, { key: 'right', tree: rightTree }].map(({ key, tree }) => (
                <Box key={key} sx={{ flex: 1, minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box', overflow: 'hidden' }}>
                  {tree && compareTreeImages.find(img => img.sessionId === tree) ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, justifyContent: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.65rem' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatDisplayDate(new Date(data.find(item => item.sessionId === tree)?.startTime))}
                        </Typography>
                        <DeselectButton onClick={() => handleCompareTreeSelection(tree)} ariaLabel={`Deselect ${key === 'left' ? 'Selection 1' : 'Selection 2'}`} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <img
                          src={compareTreeImages.find(img => img.sessionId === tree).image}
                          alt={`${key === 'left' ? 'Selection 1' : 'Selection 2'} talk tree for comparison, session on ${formatDisplayDate(new Date(data.find(item => item.sessionId === tree)?.startTime))}`}
                          style={imageStyle}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', p: 1, minHeight: isMobile ? '80px' : '200px' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : undefined }}>
                        {key === 'left' ? 'Click a tree in the Library to see it here' : 'Click another tree in the Library to see it here.'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <FlowerTable isPresentationPage={true} role="img" aria-label={generateFlowerKeyAltText()} />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h6" gutterBottom><b>{PRESENTATION_RESOURCES.tabs.compare.title}</b></Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>{PRESENTATION_RESOURCES.tabs.compare.description}</Typography>
            <Typography variant="h6" gutterBottom><b>{PRESENTATION_RESOURCES.tabs.compare.conversationStarterTitle}</b></Typography>
            <Box role="group" aria-label="Comparison conversation starters">
              {PRESENTATION_RESOURCES.tabs.compare.conversationStarters.map((starter, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedCompareConversationStarters.includes(starter)}
                      onChange={(e) => handleStarterChange(starter, e.target.checked)}
                      inputProps={{ 'aria-label': starter }}
                    />
                  }
                  label={starter}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
            <GenerateButton
              onClick={() => { if (!leftTree || !rightTree) { alert('Please select exactly 2 trees for comparison.'); return; } generateComparePresentation(); }}
              disabled={isConverting || !leftTree || !rightTree}
              label={PRESENTATION_RESOURCES.messages.generatePresentation}
            />
          </RightPanel>
        </Grid>
      </TabContentWrapper>
    );
  };

  // ── Effects  ──────────────────────────────────────
  useEffect(() => { document.title = "Student Talk Tree Viewer"; }, []);
  useEffect(() => { if (mainTabIndex === 3 && !selectedFeedbackTree && leftTree) handleFeedbackTreeSelection(leftTree); }, [mainTabIndex]);
  useEffect(() => { latestSessionIdRef.current = transcriptSessionID; }, [transcriptSessionID]);
  useEffect(() => { extractQueryParams(); }, []);
  useEffect(() => {
    if (!initialSelectionDone.current && mostRecentSession && availablePeriods.length > 0) {
      const matchingPeriod = availablePeriods.find(p => p.sessionId === mostRecentSession.sessionId);
      if (matchingPeriod) {
        setSelectedPeriod(matchingPeriod.id); setAnalyticsSessionID(mostRecentSession.sessionId);
        setTranscriptSessionID(mostRecentSession.sessionId);
        if (mostRecentSession.sessionId) handleDataRetrievalClick();
        initialSelectionDone.current = true;
      }
    }
  }, [mostRecentSession, availablePeriods]);
  useEffect(() => {
    const timeoutId = setTimeout(() => { if (selectedTutorId && selectedDate && selectedRegion) fetchAvailablePeriods(); }, 200);
    return () => clearTimeout(timeoutId);
  }, [selectedTutorId, selectedDate, selectedRegion]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={muiTheme}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'DM Sans', 'Mulish', sans-serif" }}>
        <div
          style={{ flex: 1, padding: isMobile ? '16px' : '30px', backgroundColor: PRESENTATION_RESOURCES.styles.pageBackground, overflowX: "hidden" }}
          role="main"
        >
          {(isLoading || !data.length) ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh', width: '100%' }}
              role="status" aria-live="polite" aria-busy="true" aria-label="Loading your tutoring data"
            >
              {/* Branded spinner */}
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`@keyframes tt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28C4 24.8483 4.62078 21.7274 5.82689 18.8156C7.03301 15.9038 8.80083 13.258 11.0294 11.0294C13.258 8.80083 15.9038 7.033 18.8156 5.82689C21.7274 4.62078 24.8483 4 28 4C31.1517 4 34.2726 4.62078 37.1844 5.82689C40.0962 7.03301 42.742 8.80083 44.9706 11.0294C47.1992 13.258 48.967 15.9038 50.1731 18.8156C51.3792 21.7274 52 24.8483 52 28L52 28Z" stroke="#E7E3DF" strokeWidth="8" />
                <path d="M52 28C52 31.1517 51.3792 34.2726 50.1731 37.1844C48.967 40.0962 47.1992 42.742 44.9706 44.9706C42.742 47.1992 40.0962 48.967 37.1844 50.1731C34.2726 51.3792 31.1517 52 28 52C24.8483 52 21.7274 51.3792 18.8156 50.1731C15.9038 48.967 13.258 47.1992 11.0294 44.9706C8.80083 42.742 7.033 40.0962 5.82689 37.1844C4.62078 34.2726 4 31.1517 4 28" stroke="#4C8EEC" strokeWidth="8" strokeLinecap="round" style={{ transformOrigin: '28px 28px', animation: 'tt-spin 1s linear infinite' }} />
              </svg>
              <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: '#3a3a3a', fontFamily: 'inherit' }}>
                Loading your tutoring data...
              </Typography>
            </Box>
          ) : (
            <>
              {/* ── Page header ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: isMobile ? 0 : '10px' }}>
                <Typography variant="h1" component="h1" sx={{ fontWeight: 700, fontSize: isMobile ? '26px' : '34px', fontFamily: 'inherit' }}>
                  Talk Trees
                </Typography>
                <Button
  variant="contained"
  onClick={() => window.open(
    client === 'catalyst' || client === 'teachfx' || client === 'nm'
      ? 'https://www.talktrees.ai/playbooks'
      : './Resources',
    'talkTreesReference'
  )}
  sx={{
    backgroundColor: PRESENTATION_RESOURCES.styles.buttonPrimary,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    px: isMobile ? 2 : 3,
    borderRadius: '1.5rem',
    minHeight: '40px',
    fontFamily: 'inherit',
    '&:hover': { backgroundColor: '#1558c0' },
  }}
  aria-label={client === 'catalyst' || client === 'teachfx' || client === 'nm' ? 'View Playbooks' : 'Get Started with Talk Trees Resources'}
>
  {client === 'catalyst' || client === 'teachfx' || client === 'nm' ? 'Playbooks' : 'Get Started'}
                </Button>
              </Box>

              {/* ── Accordion  ── */}
              <AccordionComponent
                inputType={inputType}
                transcriptSessionID={transcriptSessionID}
                setTranscriptSessionID={setTranscriptSessionID}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                regionList={regionList}
                tutorList={tutorList}
                setTutorList={setTutorList}
                setSelectedTutorId={setSelectedTutorId}
                handleDateChange={handleDateChange}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedTutorId={selectedTutorId}
                availablePeriods={availablePeriods}
                selectedPeriod={selectedPeriod}
                setAvailablePeriods={setAvailablePeriods}
                fetchAvailablePeriods={fetchAvailablePeriods}
                handlePeriodChange={handlePeriodChange}
                setSelectedPeriod={setSelectedPeriod}
                isStudentEngagementPage={true}
                urlTutorId={urlTutorId}
                tutorName={tutorName}
                client={client}
                mostRecentSession={mostRecentSession}
                oldestSession={oldestSession}
                handleDataRetrievalClick={handleDataRetrievalClick}
                sessionData={data.find(item => item.sessionId === (selectedPeriod ? availablePeriods.find(p => p.id === selectedPeriod)?.sessionId : null))}
              />

              {/* ── Main content ── */}
              <Box sx={{ mt: 2 }}>
                {activeVisualization === 'talkTree' && (
                  <>
                    {analyticsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column' }} role="status" aria-live="polite" aria-busy="true">
                        <CircularProgress size={40} sx={{ mb: 2 }} aria-hidden="true" />
                        <Typography variant="subtitle1" sx={{ fontFamily: 'inherit' }}>Loading analytics data...</Typography>
                      </Box>
                    ) : (
                      <>
                        {analyticsNotFoundError && (
                          <Typography color="error" sx={{ mb: 2, px: 1 }} role="alert">{analyticsNotFoundError}</Typography>
                        )}

                        {selectedPeriod && analyticstSessionID && showGraphs && !analyticsLoading && (
                          <Box sx={{ px: isMobile ? 0 : 1, mb: 4 }}>
                            <Typography variant="h2" component="h2" sx={{ fontWeight: 600, fontSize: '22px', mb: 2, fontFamily: 'inherit' }}>
                              My Student Talk Trees
                            </Typography>
                            <div role="region" aria-label="Session Trees Visualization">
                              <SessionTrees
                                processTranscriptData={processTranscriptData}
                                analyticsSessionID={analyticstSessionID}
                                previousSessionIds={previousSessionIds}
                                isStudentEngagementPage={true}
                                region={selectedRegion}
                                key={`${analyticstSessionID}-${selectedPeriod}`}
                                client={client}
                                selectedTrees={mainTabIndex === 2 ? new Set([leftTree, rightTree].filter(Boolean)) : new Set([leftTree].filter(Boolean))}
                                onTreeSelect={onTreeSelect}
                                selectionMode={mainTabIndex === 1 ? 'single' : mainTabIndex === 2 ? 'compare' : mainTabIndex === 3 ? 'single' : 'none'}
                              />
                            </div>
                          </Box>
                        )}

                        {selectedPeriod && analyticstSessionID && showGraphs && !analyticsLoading && (
                          <Paper elevation={0} sx={{ backgroundColor: 'transparent' }}>

                            {/* ── Tab bar  ── */}
                            <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
                              <Box sx={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-end', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 0 }}>
                                {!isMobile && (
                                  <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#111827', pb: 1, fontFamily: 'inherit' }}>
                                    What Can I Do With Talk Trees?
                                  </Typography>
                                )}
                                <Box
                                  role="tablist"
                                  aria-label="Talk Tree actions"
                                  sx={{ display: 'flex', width: isMobile ? '100%' : 'auto', overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}
                                >
                                  {TABS.map(({ label, Icon }, i) => {
                                    const active = mainTabIndex === i;
                                    return (
                                      <Box
                                        key={i}
                                        role="tab"
                                        aria-selected={active}
                                        aria-controls={`tabpanel-${i}`}
                                        id={`tab-${i}`}
                                        tabIndex={active ? 0 : -1}
                                        onClick={() => setMainTabIndex(i)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMainTabIndex(i); }
                                          if (e.key === 'ArrowRight') { e.preventDefault(); setMainTabIndex((i + 1) % TABS.length); }
                                          if (e.key === 'ArrowLeft') { e.preventDefault(); setMainTabIndex((i - 1 + TABS.length) % TABS.length); }
                                        }}
                                        sx={{
                                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                                          padding: isMobile ? '8px 12px' : '8px 20px',
                                          cursor: 'pointer',
                                          borderBottom: active ? '2px solid #1F72E7' : '2px solid transparent',
                                          transition: 'border-color 0.15s ease',
                                          flexShrink: 0,
                                          minWidth: isMobile ? '80px' : 'auto',
                                          '&:hover': { borderBottomColor: active ? '#1F72E7' : '#d1d5db' },
                                          '&:focus-visible': { outline: '3px solid #1F72E7', outlineOffset: '-2px', borderRadius: '4px 4px 0 0' },
                                        }}
                                      >
                                        <Icon active={active} />
                                        <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: active ? 600 : 400, color: active ? '#1F72E7' : '#6b7280', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                          {label}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Box>

                            {/* ── Tab panels ── */}
                            <Box sx={{ mt: '24px' }}>
                              {TABS.map((_, i) => (
                                <Box key={i} role="tabpanel" id={`tabpanel-${i}`} aria-labelledby={`tab-${i}`} hidden={mainTabIndex !== i}>
                                  {mainTabIndex === i && (
                                    <>
                                      {i === 0 && renderIntroductionTab()}
                                      {i === 1 && renderCelebrateTab()}
                                      {i === 2 && renderCompareTab()}
                                      {i === 3 && client !== 'teachfx' && (
                                        <ExploreMyTalk
                                          sessionData={data.find(item => item.sessionId === selectedFeedbackTree)}
                                          tutorTalkMoves={tutorTalkMoves}
                                          studentTalkMoves={studentTalkMoves}
                                          mathTopicsCovered={mathTopicsCovered}
                                          feedbackSessionHighlights={feedbackSessionHighlights}
                                          studentParticipation={studentParticipation}
                                          talkMovesLabels={talkMovesLabels}
                                          selectedTreeImage={feedbackTreeImage}
                                          processTranscriptData={processTranscriptData}
                                          selectedTreeId={selectedFeedbackTree}
                                          onTreeSelect={handleFeedbackTreeSelection}
                                          allSessionIds={[selectedFeedbackTree || leftTree, ...previousSessionIds].filter(Boolean)}
                                        />
                                      )}
                                    </>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Paper>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default StudentEngagement;